import type { IWidgetSettings, IWidgetThemeConfig } from '../types';

const CONFIG_CACHE_KEY = 'widget-config';

export interface IConfigCacheEntry {
  settings: IWidgetSettings;
  themeConfig: IWidgetThemeConfig | null;
  etag: string;
}

/**
 * Get cached config from localStorage.
 */
export function getCachedConfig(): IConfigCacheEntry | null {
  try {
    const cached = localStorage.getItem(CONFIG_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as IConfigCacheEntry;
    if (parsed && parsed.settings && typeof parsed.etag === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save config to localStorage cache.
 */
export function setCachedConfig(
  settings: IWidgetSettings,
  themeConfig: IWidgetThemeConfig | null,
  etag: string,
): void {
  try {
    const entry: IConfigCacheEntry = { settings, themeConfig, etag };
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage may be blocked or full - silent failure
  }
}

/**
 * Clear cached config from localStorage.
 */
export function clearCachedConfig(): void {
  try {
    localStorage.removeItem(CONFIG_CACHE_KEY);
  } catch {
    // Silent failure
  }
}

/**
 * Initialize config from cache.
 * Returns cached config immediately if available.
 */
export interface IConfigInitResult {
  cachedSettings: IWidgetSettings | null;
  cachedThemeConfig: IWidgetThemeConfig | null;
  cachedEtag: string | null;
}

export function initConfigFromCache(): IConfigInitResult {
  const cached = getCachedConfig();

  return {
    cachedSettings: cached?.settings || null,
    cachedThemeConfig: cached?.themeConfig || null,
    cachedEtag: cached?.etag || null,
  };
}
